import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import api from '../api';

const Register = () => {
  const { tip } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (tip !== 'ucenici' && tip !== 'studenti') {
      navigate('/');
    }
  }, [tip, navigate]);

  const isUcenici = tip === 'ucenici';
  const tipLabel = isUcenici ? 'Ученици' : 'Студенти';
  const tipValue = isUcenici ? 'ученици' : 'студенти';

  const [teamName, setTeamName] = useState('');
  const [memberCount, setMemberCount] = useState(2);
  const [members, setMembers] = useState([
    { име: '', презиме: '', јеКапитен: true, пpiости: false },
    { име: '', презиме: '', јеКапитен: false, пpiости: false }
  ]);
  const [captainPhone, setCaptainPhone] = useState('');
  const [captainEmail, setCaptainEmail] = useState('');
  const [napomena, setNapomena] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleMemberCountChange = (newCount) => {
    const count = parseInt(newCount);
    if (count < 2 || count > 5) return;
    setMemberCount(count);
    setMembers(prev => {
      const updated = [...prev];
      if (count > prev.length) {
        for (let i = prev.length; i < count; i++) {
          updated.push({ име: '', презиме: '', јеКапитен: false, пpiости: false });
        }
      } else {
        const sliced = updated.slice(0, count);
        if (!sliced.some(m => m.јеКапитен)) sliced[0].јеКапитен = true;
        return sliced;
      }
      return updated;
    });
  };

  const handleMemberChange = (index, field, value) => {
    setMembers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleCaptainSelect = (index) => {
    setMembers(prev => prev.map((m, i) => ({ ...m, јеКапитен: i === index })));
  };

  const validate = () => {
    const newErrors = {};
    if (!teamName.trim()) newErrors.teamName = 'Име тима је обавезно';
    if (teamName.trim().length > 100) newErrors.teamName = 'Максимум 100 карактера';
    members.forEach((m, i) => {
      if (!m.име.trim()) newErrors[`member_${i}_ime`] = 'Обавезно';
      if (!m.презиме.trim()) newErrors[`member_${i}_prezime`] = 'Обавезно';
    });
    if (!members.some(m => m.јеКапитен)) newErrors.captain = 'Изаберите капитена';
    if (!captainPhone.trim()) newErrors.phone = 'Обавезно';
    else if (!/^[\d\s\+\-\(\)]{6,20}$/.test(captainPhone.trim())) newErrors.phone = 'Невалидан формат';
    if (!captainEmail.trim()) newErrors.email = 'Обавезно';
    else if (!/^\S+@\S+\.\S+$/.test(captainEmail.trim())) newErrors.email = 'Невалидан формат';
    if (napomena.length > 500) newErrors.napomena = 'Максимум 500 карактера';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error('Исправите грешке у форми'); return; }
    setLoading(true);
    try {
      const payload = {
        имеТима: teamName.trim(),
        типТима: tipValue,
        бројЧланова: memberCount,
        чланови: members.map(m => ({
          име: m.име.trim(),
          презиме: m.презиме.trim(),
          јеКапитен: m.јеКапитен,
          пpiости: m.пpiости
        })),
        капитен: { телефон: captainPhone.trim(), емаил: captainEmail.trim().toLowerCase() },
        напомена: napomena.trim()
      };
      await api.post('/teams', payload);
      navigate('/uspeh', { state: { teamName: teamName.trim(), tip: tipLabel } });
    } catch (error) {
      const msg = error.response?.data?.message || 'Грешка при пријави.';
      const errs = error.response?.data?.errors;
      if (errs?.length) errs.forEach(e => toast.error(e));
      else toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fastingCount = members.filter(m => m.пpiости).length;

  return (
    <div className="form-page">
      <motion.div
        className="form-container"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Logos */}
        <div className="form-logos">
          {[
            { src: 'https://res.cloudinary.com/dnx6y0737/image/upload/v1772574166/ftnkm_wnd24z.png', alt: 'ФТН КМ', link: 'https://ftn.pr.ac.rs/' },
            { src: 'https://res.cloudinary.com/dnx6y0737/image/upload/v1772574898/gimnmnbez_yxcyal.png', alt: 'Гимназија КМ', link: 'https://gimnazijakm.edu.rs/' },
            
            { src: 'https://res.cloudinary.com/dnx6y0737/image/upload/v1772574898/pmfbez_du25uh.png', alt: 'ПМФ КМ', link: 'https://pmf.pr.ac.rs/' },
          ].map((logo, i) => (
            <div key={i} className="logo-placeholder">
              <a href={logo.link} target="_blank" rel="noopener noreferrer">
                <img src={logo.src} alt={logo.alt} />
              </a>
            </div>
          ))}
        </div>

        {/* Back button */}
        <div className="form-header">
          <button className="form-back" onClick={() => navigate('/')}>
            ← Назад на почетну
          </button>
          <h1 className={`form-title ${isUcenici ? 'ucenici' : 'studenti'}`}>
            Пријава — {tipLabel}
          </h1>
        </div>

        <div className="form-card">
          <form onSubmit={handleSubmit} noValidate>
            {/* Team Info */}
            <div className="form-section">
              <div className="form-section-title">Подаци о тиму</div>
              <p style={{ fontSize: '0.65rem', fontStyle: 'italic', color: '#6b6b8d', marginBottom: '12px', lineHeight: 1.5 }}>
                  Уколико члан пости, означите поље пости. Уколико је капитен, означите поље капитен (1 по тиму).
              </p>
              <div className="form-row-2col">
                <div className="form-group">
                  <label className="form-label">Име тима *</label>
                  <input
                    type="text"
                    className={`form-input ${errors.teamName ? 'error' : ''}`}
                    placeholder="Назив вашег тима"
                    value={teamName}
                    onChange={e => setTeamName(e.target.value)}
                    maxLength={100}
                  />
                  {errors.teamName && <div className="form-error">{errors.teamName}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Број чланова *</label>
                  <select
                    className="form-select"
                    value={memberCount}
                    onChange={e => handleMemberCountChange(e.target.value)}
                  >
                    <option value={2}>2 члана</option>
                    <option value={3}>3 члана</option>
                    <option value={4}>4 члана</option>
                    <option value={5}>5 чланова</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Members */}
            <div className="form-section">
              <div className="form-section-title">
                Чланови тима
                {fastingCount > 0 && (
                  <span className="fasting-badge">🍽 Посте: {fastingCount}/{memberCount}</span>
                )}
              </div>
              {members.map((member, index) => (
                <motion.div
                  key={index}
                  className="member-row"
                  initial={{ opacity: 0, x: -15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.07 }}
                >
                  <div className="member-row-header">
                    <span className="member-number">Члан {index + 1}</span>
                    <div className="member-row-toggles">
                      <label className="fasting-toggle">
                        <input
                          type="checkbox"
                          checked={member.пpiости}
                          onChange={e => handleMemberChange(index, 'пpiости', e.target.checked)}
                        />
                        <span className="fasting-label">🍽 Пости</span>
                      </label>
                      <label className="captain-toggle">
                        <input
                          type="radio" name="captain"
                          checked={member.јеКапитен}
                          onChange={() => handleCaptainSelect(index)}
                        />
                        <span className="captain-label">👑 Капитен</span>
                      </label>
                    </div>
                  </div>
                  <div className="member-fields">
                    <div className="form-group">
                      <input
                        type="text"
                        className={`form-input ${errors[`member_${index}_ime`] ? 'error' : ''}`}
                        placeholder="Име"
                        value={member.име}
                        onChange={e => handleMemberChange(index, 'име', e.target.value)}
                        maxLength={50}
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        className={`form-input ${errors[`member_${index}_prezime`] ? 'error' : ''}`}
                        placeholder="Презиме"
                        value={member.презиме}
                        onChange={e => handleMemberChange(index, 'презиме', e.target.value)}
                        maxLength={50}
                      />
                    </div>
                  </div>
                  {member.јеКапитен && (
                    <motion.div
                      className="captain-fields"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                    >
                      <div className="form-group">
                        <input
                          type="tel"
                          className={`form-input ${errors.phone ? 'error' : ''}`}
                          placeholder="📱 Телефон капитена"
                          value={captainPhone}
                          onChange={e => setCaptainPhone(e.target.value)}
                          maxLength={20}
                        />
                        {errors.phone && <div className="form-error">{errors.phone}</div>}
                      </div>
                      <div className="form-group">
                        <input
                          type="email"
                          className={`form-input ${errors.email ? 'error' : ''}`}
                          placeholder="📧 E-mail капитена"
                          value={captainEmail}
                          onChange={e => setCaptainEmail(e.target.value)}
                          maxLength={100}
                        />
                        {errors.email && <div className="form-error">{errors.email}</div>}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
              {errors.captain && <div className="form-error">{errors.captain}</div>}
            </div>

            {/* Note */}
            <div className="form-section">
              <div className="form-section-title">Напомена (опционо)</div>
              <div className="form-group">
                <textarea
                  className={`form-input form-textarea ${errors.napomena ? 'error' : ''}`}
                  placeholder="Ако имате било какву напомену у вези исхране (ако постите на води), алергена (можда свињског меса), или нечег другог — молимо вас да то овде наведете."
                  value={napomena}
                  onChange={e => setNapomena(e.target.value)}
                  maxLength={500}
                  rows={3}
                />
                <div className="textarea-counter">{napomena.length}/500</div>
                {errors.napomena && <div className="form-error">{errors.napomena}</div>}
              </div>
            </div>

            <button
              type="submit"
              className={`form-submit ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? 'Слање...' : '🚀 Пријави тим 🚀'}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;
